import { useRef, useState } from "react";
import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";
import {
  dropIndexFromCenters,
  FOOTER_ZONE_ID,
  insertKeyAt,
  nextStoredFooterActions,
  OPTIONS_ZONE_ID,
  isMainKey,
  type FooterDragData,
  type FooterDragSource,
} from "./footerPinning";

type DropTarget = { zone: "footer"; index: number } | { zone: "options" };

interface ActiveDrag {
  actionKey: string;
  source: FooterDragSource;
  /** Центровете (по X) на ОСТАНАЛИТЕ бутони в лентата при старта. */
  centers: number[];
}

/** Как се рисува бутон в лентата: нормално, като място за пускане
 *  (пунктир) или скрит — влаченият бутон е над „Опции". */
export type FooterItemState = "normal" | "placeholder" | "hidden";

interface UseFooterDragParams {
  /** Целият ред на лентата в момента — основни и изнесени бутони. */
  orderKeys: string[];
  storedKeys: readonly string[];
  barRef: React.RefObject<HTMLDivElement | null>;
  onSave: (keys: string[]) => void;
}

const sameTarget = (left: DropTarget | null, right: DropTarget | null) =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.zone === right.zone &&
    (left.zone === "options" ||
      (right.zone === "footer" && left.index === right.index)));

const sameKeys = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

/**
 * Влаченето между „Опции" и лентата под масите (dnd-kit); основните бутони
 * се местят само в лентата (`isMainKey`). Лентата не е
 * sortable списък: мястото за пускане се смята по центровете на бутоните
 * ОТ ПРЕДИ влаченето (`dropIndexFromCenters`) — всички бутони, и основните,
 * така че изнесен бутон може да застане и пред тях, и между тях; на мястото
 * се рисува пунктирен бутон. Записът е личната настройка `footerActions`.
 */
export const useFooterDrag = ({
  orderKeys,
  storedKeys,
  barRef,
  onSave,
}: UseFooterDragParams) => {
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  // Събитията на dnd-kit идват по-бързо от рендерите — центровете се четат
  // от ref, не от затвореното в handler-а състояние.
  const activeDragRef = useRef<ActiveDrag | null>(null);

  const targetOf = (
    event: DragMoveEvent | DragEndEvent,
  ): DropTarget | null => {
    const overId = event.over?.id;
    const drag = activeDragRef.current;
    // Основен бутон (Нова маса · Добави · Закрий · Опции) само се подрежда —
    // над „Опции" не се пуска (не се крие), остава на мястото си.
    if (overId === OPTIONS_ZONE_ID) {
      return drag && isMainKey(drag.actionKey) ? null : { zone: "options" };
    }
    if (overId !== FOOTER_ZONE_ID || !drag) return null;
    const start = getEventCoordinates(event.activatorEvent);
    if (!start) return null;
    return {
      zone: "footer",
      index: dropIndexFromCenters(drag.centers, start.x + event.delta.x),
    };
  };

  const reset = () => {
    activeDragRef.current = null;
    setActiveDrag(null);
    setDropTarget(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as FooterDragData | undefined;
    if (!data) return;
    const centers = Array.from(
      barRef.current?.querySelectorAll<HTMLElement>("[data-footer-item]") ??
        [],
    )
      .filter((element) => element.dataset.footerItem !== data.actionKey)
      .map((element) => {
        const rect = element.getBoundingClientRect();
        return rect.left + rect.width / 2;
      });
    const drag = { actionKey: data.actionKey, source: data.source, centers };
    activeDragRef.current = drag;
    setActiveDrag(drag);
    setDropTarget(null);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const next = targetOf(event);
    setDropTarget((previous) => (sameTarget(previous, next) ? previous : next));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const drag = activeDragRef.current;
    const target = targetOf(event);
    reset();
    if (!drag || !target) return;

    let nextKeys: string[] | null = null;
    if (target.zone === "footer") {
      const orderAfter = insertKeyAt(orderKeys, drag.actionKey, target.index);
      nextKeys = nextStoredFooterActions(storedKeys, orderAfter);
    } else if (drag.source === "footer") {
      const orderAfter = orderKeys.filter((key) => key !== drag.actionKey);
      nextKeys = nextStoredFooterActions(storedKeys, orderAfter, drag.actionKey);
    }
    if (nextKeys && !sameKeys(nextKeys, storedKeys)) onSave(nextKeys);
  };

  // Лентата в момента: при влачене — с пунктирен бутон на мястото за пускане.
  let displayKeys = orderKeys;
  if (activeDrag && dropTarget?.zone === "footer") {
    displayKeys = insertKeyAt(orderKeys, activeDrag.actionKey, dropTarget.index);
  }
  const stateOf = (key: string): FooterItemState => {
    if (key !== activeDrag?.actionKey) return "normal";
    return dropTarget?.zone === "options" ? "hidden" : "placeholder";
  };

  return {
    footerItems: displayKeys.map((key) => ({ key, state: stateOf(key) })),
    /** Ключът на влаченото действие — за DragOverlay и затъмняването в „Опции". */
    activeKey: activeDrag?.actionKey,
    activeSource: activeDrag?.source,
    /** Бутон е над панела „Опции" — ако е от лентата, ще се върне там. */
    overOptions: dropTarget?.zone === "options",
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel: reset,
  };
};
